import SpinnerLoader from "@app/neva/components/spinnerLoader/spinnerLoader";
import { Button } from "@natera/form";
import React, { FC, useContext, useEffect, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingContext } from "@natera/platform/lib/components/context";
import { Link } from "@natera/navigation";
import { useMutation } from "@apollo/client";
import IntakeFormService, {
  CreateOncUppGisTriageResponse,
} from "@app/service/intakeForm";
import {
  clearFormStateCache,
  getGisIntakeFormStateCacheKey,
} from "@app/pages/private/scheduleAGeneticInformationSession/context";

import { TestCardStatus, TestType } from "@app/provider/testData/types";
import { classifyCounselingIntent } from "@app/service/intakeForm";
import {
  COUNSELING_INTENT_LABELS,
  getAcuityAppointmentConfig,
  getResultSpecificAcuityDirectLink,
  resolveAcuityLookupType,
} from "@app/pages/private/scheduleAGeneticInformationSession/buildAcuityUrl";
import {
  ConfigContext,
  IntlContext,
  Language,
  NotificationContext,
  ProfileContext,
  TestResultContext,
  ViewResultContext,
} from "@app/provider";
import { routes } from "@app/routing";
import { isOncologyPortal, openNewWindow } from "@app/utils";
import { getGisScheduleLink } from "@app/utils/getGisScheduleLink";
import { GIS_SCHEDULING_SOURCE_TEST_RESULT } from "@app/utils/gisEntrySource";
import SubCard from "@app/components/subCard/subCard";
import { getResultReleasedAtMessage } from "@app/utils/getResultReleasedAtMessage";
import useTestResultsTexts from "@app/hooks/useTestResultsTexts";
import SupplementalMaterialsForWH from "./supplementalMaterialsForWH";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import { BlueHeaderContent } from "@app/components/ui/layout";
import PreTestInfo from "./preTestInfo";
import { getProductLink } from "@app/utils/getProductLink";
import { getReturnRouteFromOrigin } from "@app/utils/navigationOrigin";
import { TestDetailsContext } from "@app/provider/testData";
import { NevaWrapper } from "@app/neva/nevaWrapper";
import { GenderOption, TestResult } from "@app/provider/testResult";
import MyResultCard from "@app/components/myResultCard";
import CancerTypeBlock from "@app/components/cancerTypeBlock";

import Message from "@assets/svg/icons/message2.svg";
import PortalsComputerSupport from "@assets/svg/portals_computer_support.svg";

import DownloadableResultsGuideSubCard from "../downloadableResultsGuide/downloadableResultsGuideSubCard";
import "./testResultDetails.scss";
import { BusinessUnits } from "@app/provider/drawRequest";
import { Config } from "@app/provider/config";
import { WidgetBanner } from "@app/pippa/widgetBanner";
import PippaWidget from "@app/pippa/pippaWidget";
import { PippaIngressSource } from "@app/pippa/models/analytics";

const messages = defineMessages({
  received: {
    id: "received",
    defaultMessage: "Received",
  },
  receivedOn: {
    id: "receivedOn",
    defaultMessage: "Received on {date}",
  },
  viewResults: {
    id: "viewResults",
    defaultMessage: "View results",
  },
  additionalDetails: {
    id: "additionalDetails",
    defaultMessage: "Additional details",
  },
  exploreRelatedResources: {
    id: "exploreRelatedResources",
    defaultMessage: "Explore related resources",
  },
  moreAbout: {
    id: "moreAboutProduct",
    defaultMessage: "More about\n {product}",
  },
  scheduleSession: {
    id: "scheduleSession",
    defaultMessage: "Schedule a genetic information session",
  },
  shareFetalSexButton: {
    id: "shareFetalSexButton",
    defaultMessage: "Share fetal sex with someone else",
  },
  backToResultDetails: {
    id: "backToResultDetails",
    defaultMessage: "Results Detail",
  },
  myResults: {
    id: "myResults",
    defaultMessage: "My Results",
  },
});

export const onScheduleSession = (
  testResult: TestResult,
  config: Config,
  currentLanguage: Language,
  scheduleLinkOverride?: string
): void => {
  const link =
    scheduleLinkOverride ??
    getGisScheduleLink(testResult.testType, config, currentLanguage);
  openNewWindow(link);
  heap.track(HEAP_EVENTS.upp_click_gis, {
    location: HeapEventLocation.resultDetailPage,
    test_type: testResult.testType,
  });
};

export const acuityIFrameEnabled = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  return params.get("acuityIFrame") === "true";
};

/**
 * Router `location.state`: the back-navigation target (see
 * `getReturnRouteFromOrigin`) plus the one-shot flag the result PDF page sets
 * on close to auto-open the chatbot. The flag is stripped once consumed.
 */
type TestResultDetailsLocationState = {
  from?: string;
  autoOpenPippa?: boolean;
};

const ACUITY_IDS: Record<
  "empower" | "oncology" | "default",
  {
    owner: string;
    schedulingSourceFieldID: string;
    counselingIntentFieldID: string;
  }
> = {
  // Empower always uses WH
  empower: {
    owner: "11361661",
    schedulingSourceFieldID: "17697279",
    counselingIntentFieldID: "18565665",
  },
  // Field ID for "Source" field in Acuity
  oncology: {
    owner: "27869891",
    schedulingSourceFieldID: "17904034",
    counselingIntentFieldID: "18565637",
  },
  default: {
    owner: "11361661",
    schedulingSourceFieldID: "17697279",
    counselingIntentFieldID: "18565665",
  },
};

function getAcuitySchedulingIds(
  testType: TestType,
  businessUnit: BusinessUnits
): {
  owner: string;
  schedulingSourceFieldID: string;
  counselingIntentFieldID: string;
} {
  const key: "empower" | "oncology" | "default" =
    testType === TestType.EMPOWER
      ? "empower"
      : businessUnit === BusinessUnits.ONCOLOGY
        ? "oncology"
        : "default";
  return ACUITY_IDS[key];
}

const TestResultDetailsPage: FC = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as
    | TestResultDetailsLocationState
    | undefined;
  const { currentLanguage } = useContext(IntlContext);
  const { config } = React.useContext(ConfigContext);
  const [showScheduleSession, setShowScheduleSession] = React.useState(false);
  const [iframeLoading, setIframeLoading] = React.useState(true);
  const scheduleSessionRef = useRef<HTMLDivElement>(null);

  const [isWidgetOpen, setIsWidgetOpen] = React.useState(
    () => locationState?.autoOpenPippa === true
  );
  const [pippaIngressSource, setPippaIngressSource] = React.useState(() =>
    locationState?.autoOpenPippa === true
      ? PippaIngressSource.AUTO_OPEN_POST_PDF
      : PippaIngressSource.AI_ASSISTANT_BANNER
  );

  // Scroll to the schedule session section when it is shown
  useEffect(() => {
    if (showScheduleSession) {
      setIframeLoading(true);
    }

    if (showScheduleSession && scheduleSessionRef.current) {
      scheduleSessionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showScheduleSession]);

  const {
    isLoading,
    getTestDetails,
    getOrderUidPublic,
    getTestUid,
    refetchTest,
  } = useContext(TestDetailsContext);
  const {
    testResult,
    getTestResult,
    getTestResultError,
    isLoading: isLoadingResults,
  } = useContext(TestResultContext);
  const { addNotification } = useContext(NotificationContext);

  const { openResultPage, openShareFetalSexPage } =
    useContext(ViewResultContext);

  const testResultsTexts = useTestResultsTexts();
  const testCard = getTestDetails();
  const testUid = getTestUid();
  const orderUidPublic = getOrderUidPublic();

  const { isLoading: isLoadingProfile } = React.useContext(ProfileContext);

  const isPageLoading = isLoading || isLoadingResults || isLoadingProfile;

  const testTitle =
    testResult?.testType &&
    testResultsTexts.getTestResultDetailsTitle(testResult?.testType);
  const testSubTitle =
    testResult?.testType &&
    testResultsTexts.getTestResultDetailsSubTitle(testResult?.testType);
  const isPippaBannerEnabled = testCard?.isPippaEnabled === true;

  useEffect(() => {
    if (orderUidPublic && testUid && !getTestResultError && !isLoadingResults) {
      getTestResult(orderUidPublic, testUid);
    }
  }, []);

  useEffect(() => {
    if (getTestResultError) {
      addNotification({ type: "error" });
    }
  }, [getTestResultError]);

  // Retires the flag on the open -> closed transition only. Deliberately keyed
  // on isWidgetOpen alone: re-running on locationState changes would re-strip
  // mid-rebuild and lose the state the details page is rebuilt from.
  useEffect(() => {
    if (isWidgetOpen || !locationState?.autoOpenPippa) return;

    navigate(location.pathname, {
      replace: true,
      state: { from: locationState.from },
    });
  }, [isWidgetOpen]);

  const [createGisTriageSession] = useMutation<CreateOncUppGisTriageResponse>(
    IntakeFormService.createOncUppGisTriage()
  );

  // Check if testResult or testCard is not available
  if (!testResult || !testCard) {
    return null;
  }

  const openResultPdf = async (documentUid: string) => {
    let testDetails = testCard;
    if (testCard?.status === TestCardStatus.RESULT_READY) {
      testDetails = await refetchTest();
    }
    testDetails &&
      openResultPage({ ...testDetails, latestResultDocumentUid: documentUid });
  };

  const backButtonRoute = getReturnRouteFromOrigin(locationState, routes.home);

  const resultReleasedAt = getResultReleasedAtMessage(
    testCard,
    intl,
    messages.receivedOn,
    messages.received
  );

  const displayShareFetalSexButton =
    testResult.testType === TestType.PANORAMA &&
    testResult.releasedToPatientDate &&
    !testResult.caPnsSupplemental &&
    testResult?.fetalSex &&
    [
      GenderOption.FEMALE,
      GenderOption.MALE,
      GenderOption.TWO_FEMALES,
      GenderOption.TWO_MALES,
      GenderOption.A_MALE_AND_A_FEMALE,
    ].includes(testResult.fetalSex);

  const onShareFetalSexClick = () => {
    openShareFetalSexPage(testCard);
    heap.track(HEAP_EVENTS.upp_fetalsexreporting_click, {
      lims_clinic_id: testCard.clinic?.limsId,
      public_order_uid: testCard.orderUidPublic,
    });
  };

  const documentsToDisplay = testResult.resultDocuments;

  const productLink = getProductLink({
    testResult,
    productLinks: config.links.product,
  });

  //todo temporary solution
  const displaySubCard = ![
    TestType.SINGLE_GENE_NIPT,
    TestType.LATITUDE,
  ].includes(testResult.testType);

  const moreAboutClickHandler = () => {
    openNewWindow(productLink);
  };

  const acuityResultCode = testResult.resultCode ?? testCard.resultCode;

  const acuityLookupType = resolveAcuityLookupType({
    testType: testResult.testType,
    status: testCard.status,
    resultCode: acuityResultCode,
  });

  // Counseling intent (pre-test vs post-test) is derived from the test
  // card status here because this iframe path bypasses the intake form
  // flow. RESULT_READY / TEST_COMPLETE → post-test; everything else → pre-test.
  const counselingIntent = classifyCounselingIntent(testCard.status);

  const acuityMapping = getAcuityAppointmentConfig(
    acuityLookupType,
    counselingIntent,
    acuityResultCode
  );

  if (!acuityMapping) {
    console.error(
      `No acuity appointment mapping found for test type: ${testResult.testType}`
    );
  }

  // Result-aware products resolve to specialized appointment types first; all
  // other cases fall back to their generic `acuity_map.json` entry.
  const acuityID: string =
    acuityMapping?.["acuityAppointmentTypeID"].toString() ?? "";

  const resultSpecificAcuityDirectLink = getResultSpecificAcuityDirectLink(
    testResult.testType,
    acuityMapping
  );

  const { owner, schedulingSourceFieldID, counselingIntentFieldID } =
    getAcuitySchedulingIds(testResult.testType, testResult.businessUnit);

  const counselingIntentLabel = COUNSELING_INTENT_LABELS[counselingIntent];

  const isScheduleSessionExpandable =
    config.gisIFrame.enabled || acuityIFrameEnabled();

  const widgetBannerButtonHandler = () => {
    setPippaIngressSource(PippaIngressSource.AI_ASSISTANT_BANNER);
    setIsWidgetOpen(true);
  };

  return (
    <LoadingContext isLoading={isPageLoading}>
      <BlueHeaderContent
        blueHeaderTitle={intl.formatMessage(messages.backToResultDetails)}
        backArrowHeaderRoute={backButtonRoute}
        medium={true}
      >
        <section className="result-card" data-testid="result-details-page">
          <div className="result-card-header">
            <h2 className="result-test-title">{testTitle}</h2>
            <h3 className="result-sub-title">{testSubTitle}</h3>
            {testCard.clinicalInfo?.cancerType ||
            testCard.clinicalInfo?.dateOfSurgery ? (
              <CancerTypeBlock
                condensed
                cancerType={testCard.clinicalInfo?.cancerType}
                dateOfSurgery={testCard.clinicalInfo?.dateOfSurgery}
              />
            ) : (
              <h3 className="result-sub-title">{resultReleasedAt}</h3>
            )}
          </div>
          <div className="result-card-main">
            <h3>{intl.formatMessage(messages.myResults)}</h3>
            {documentsToDisplay && (
              <>
                {documentsToDisplay.map((doc) => {
                  return (
                    <MyResultCard
                      key={doc.document?.uid}
                      doc={doc}
                      openResultPdf={openResultPdf}
                    />
                  );
                })}
              </>
            )}
          </div>
          <div className="result-card-footer">
            <SupplementalMaterialsForWH testResult={testResult} />
            <WidgetBanner
              showWidgetBanner={isPippaBannerEnabled}
              onButtonClickHandler={widgetBannerButtonHandler}
              isWidgetOpen={isWidgetOpen}
            />
            <h3>{intl.formatMessage(messages.exploreRelatedResources)}</h3>
            <div className="row">
              {displaySubCard && (
                <Link to={productLink} target="_blank">
                  <SubCard
                    icon={PortalsComputerSupport}
                    title={intl.formatMessage(messages.moreAbout, {
                      product: testTitle,
                    })}
                    onClick={moreAboutClickHandler}
                  />
                </Link>
              )}
              <PreTestInfo testResult={testResult} />
              <DownloadableResultsGuideSubCard testResult={testResult} />
            </div>
            <div className="button-container">
              {testResult.testType !== TestType.PROSPERA && (
                <Button
                  onClick={() => {
                    if (
                      config.testSelfEducation.enabled &&
                      isOncologyPortal(testResult.testType)
                    ) {
                      const orderUid = testCard.orderUidPublic || "";
                      const selectedTestUid = testCard.uid || "";
                      clearFormStateCache(
                        getGisIntakeFormStateCacheKey(orderUid, selectedTestUid)
                      );
                      heap.track(HEAP_EVENTS.upp_click_gis, {
                        location: HeapEventLocation.resultDetailPage,
                        test_type: testResult.testType,
                      });
                      // Fire-and-forget: create the triage row up-front so
                      // subsequent intake steps can attach data to it. Failure
                      // is non-blocking — the schedule page will fall back to
                      // looking up the active session.
                      void createGisTriageSession({
                        variables: {
                          orderUidPublic: orderUid,
                          testUid: selectedTestUid,
                          schedulingSource: GIS_SCHEDULING_SOURCE_TEST_RESULT,
                        },
                      }).catch((err) => {
                        console.error(
                          "Failed to create GIS intake session:",
                          err
                        );
                      });
                      navigate(
                        routes.scheduleAGeneticInformationSessionIntakePage(
                          orderUid,
                          selectedTestUid
                        )
                      );
                    } else if (acuityID && isScheduleSessionExpandable) {
                      setShowScheduleSession(!showScheduleSession);
                    } else {
                      onScheduleSession(
                        testResult,
                        config,
                        currentLanguage,
                        resultSpecificAcuityDirectLink
                      );
                    }
                  }}
                  icon={Message}
                  className="btn-schedule-session"
                  outlined
                  {...(isScheduleSessionExpandable && {
                    "aria-controls": "schedule-session-content",
                    "aria-expanded": showScheduleSession,
                  })}
                >
                  {intl.formatMessage(messages.scheduleSession)}
                </Button>
              )}
            </div>
            {showScheduleSession && (
              <div
                id="schedule-session-content"
                className="schedule-session-section"
                ref={scheduleSessionRef}
              >
                {iframeLoading && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px",
                    }}
                  >
                    <SpinnerLoader />
                  </div>
                )}
                <iframe
                  src={`https://app.acuityscheduling.com/schedule.php?owner=${owner}&appointmentType=${acuityID}&field:${schedulingSourceFieldID}=testResultDetailsPage&field:${counselingIntentFieldID}=${encodeURIComponent(counselingIntentLabel)}`}
                  width="100%"
                  height="800"
                  frameBorder="0"
                  allow="payment"
                  onLoad={() => setIframeLoading(false)}
                  style={{ display: iframeLoading ? "none" : "block" }}
                ></iframe>{" "}
                <script
                  src="https://embed.acuityscheduling.com/js/embed.js"
                  type="text/javascript"
                ></script>
              </div>
            )}
            <div className="button-container">
              {displayShareFetalSexButton && (
                <Button
                  onClick={onShareFetalSexClick}
                  className="btn-share-fetal-sex"
                  outlined
                >
                  {intl.formatMessage(messages.shareFetalSexButton)}
                </Button>
              )}
            </div>
          </div>
        </section>
      </BlueHeaderContent>
      <NevaWrapper />
      <PippaWidget
        isWidgetOpen={isWidgetOpen}
        setIsWidgetOpen={setIsWidgetOpen}
        ingressSource={pippaIngressSource}
      />
    </LoadingContext>
  );
};
export default TestResultDetailsPage;
